<template>
  <div
    v-if="hasAmplify"
    :key="`mention-${mention.id}-${totalSocialAmplifyVolume}`"
    class="row items-baseline"
    data-testid="social-amplify-widget"
  >
    <div
      :class="[
        'tw:flex tw:flex-nowrap',
        { 'tw:cursor-pointer': !isPublicInsights },
      ]"
      data-testid="social-amplify-click-container"
      @click="onSocialAmplifyClicked"
    >
      <BaseButton
        :class="{ 'tw:ml-0.5 tw:mr-1': !dense }"
        dense
        :disabled="!totalSocialAmplifyVolume || isPublicInsights"
        flat
        icon="chart-pie"
        new-design
        new-icon
      />

      <div :class="dense ? 'tw:text-xxs' : 'tw:text-base tw:leading-5'">
        {{ getLocaleText("social_amplify_icon.social_amplify") }}

        <div :class="['tw:text-left tw:font-bold', { 'tw:text-xxs': dense }]">
          {{ totalSocialEngagementFormatted }}
        </div>
      </div>

      <BaseStreemTooltip
        v-if="displayTooltip"
        anchor="center right"
        :offset="[4, 4]"
        self="center left"
      >
        <span v-if="!totalSocialAmplifyVolume && !enoughTimeHasPassed">
          {{
            getLocaleText(
              "social_amplify_icon.not_enough_social_data_recent_article"
            )
          }}
        </span>
        <span v-else-if="!totalSocialAmplifyVolume">
          {{ getLocaleText("social_amplify_icon.not_enough_social_data") }}
        </span>
      </BaseStreemTooltip>
    </div>

    <SocialAmplifyPopup
      v-if="totalSocialAmplifyVolume"
      v-model="showSocialAmplifyPopup"
      :mention="mention"
      :root-element="rootElement"
      :total="totalSocialAmplifyVolume"
    />

    <BaseStreemTooltip
      v-if="totalSocialAmplifyVolume && !showSocialAmplifyPopup"
    >
      {{ getLocaleText("social_amplify_popup.social_amplify_tooltip") }}
    </BaseStreemTooltip>
  </div>
</template>

<script>
import { differenceInMinutes } from "date-fns/differenceInMinutes";
import { inject } from "vue";

import { getLocaleText } from "shared/boot/i18n";
import { BaseButton, BaseStreemTooltip } from "shared/components/base";
import SocialAmplifyPopup from "shared/components/SocialAmplifyPopup.vue";
import useSocialAmplify from "shared/composables/useSocialAmplify";
import { suffix } from "shared/helpers/number";
import { IsPublicInsightsSymbol } from "shared/modules/Charts/types/symbols";

const MINIMUM_MINUTES_TO_ELAPSE = 15;

export default {
  name: "SocialAmplifyWidget",
  components: {
    BaseButton,
    SocialAmplifyPopup,
    BaseStreemTooltip,
  },
  props: {
    mention: {
      type: Object,
      required: true,
    },
    isListView: {
      type: Boolean,
    },
    isParentVisible: {
      type: Boolean,
    },
    rootElement: {
      type: HTMLElement,
      default: null,
    },
    dense: {
      type: Boolean,
    },
    scrollIntoView: {
      type: Boolean,
    },
  },
  emits: ["click", "update-social-amplify-widget-key"],
  setup(props) {
    const isPublicInsights = inject(IsPublicInsightsSymbol, false);

    const {
      showSocialAmplifyPopup,
      hasAmplify,
      totalSocialAmplifyVolume,
      toggleSocialAmplify,
    } = useSocialAmplify(props);

    return {
      isPublicInsights,
      showSocialAmplifyPopup,
      hasAmplify,
      totalSocialAmplifyVolume,
      toggleSocialAmplify,
    };
  },
  computed: {
    minutesElapsedSincePublish() {
      const now = new Date();
      const publishedAt = new Date(this.mention.published_at);

      return differenceInMinutes(now, publishedAt);
    },
    enoughTimeHasPassed() {
      return this.minutesElapsedSincePublish >= MINIMUM_MINUTES_TO_ELAPSE;
    },
    totalSocialEngagementFormatted() {
      return suffix(this.totalSocialAmplifyVolume);
    },
    displayTooltip() {
      return !this.totalSocialAmplifyVolume || !this.enoughTimeHasPassed;
    },
  },
  watch: {
    isParentVisible: {
      handler(value) {
        if (value === false) {
          this.showSocialAmplifyPopup = false;
        }
      },
    },
    totalSocialAmplifyVolume: {
      deep: true,
      handler(value) {
        this.$emit("update-social-amplify-widget-key", value);
      },
    },
  },
  mounted() {},
  methods: {
    getLocaleText,
    onSocialAmplifyClicked() {
      if (this.isPublicInsights) {
        return;
      }

      if (!this.showSocialAmplifyPopup) {
        if (this.scrollIntoView && this.totalSocialAmplifyVolume) {
          this.rootElement.scrollIntoView({ behaviour: "smooth" });
        }

        this.$track("Opened Social Amplify");
      }

      this.toggleSocialAmplify();
    },
  },
};
</script>
